import { atom, useAtomValue, useSetAtom } from "jotai";
import { type JSX, useEffect } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxIcon,
  ComboboxInput,
  ComboboxItem,
  ComboboxTrigger,
  ComboboxValue,
} from "@frontend/components/atoms/Combobox";
import { Input } from "@frontend/components/atoms/Input";
import { useLingui } from "@lingui/react/macro";

import { useAddressText } from "../hooks/useAddressText";
import { loadStatesAtom, statesAtom } from "../stores";
import type { AddressFieldProps, AddressTextField } from "../types";
import type { AddressState } from "../types";
import { FieldFrame } from "./FieldFrame";

const NO_COUNTRY = atom<string | null | undefined>("");

export type StateFieldProps = AddressFieldProps & {
  /** Which country's states are on offer. */
  countryField?: AddressTextField;
};

/**
 * The canton, department or province, picked from the states of the country.
 * What is stored is the code the address holds, `VD` rather than `Vaud`.
 *
 * A country the referential knows no state for leaves a plain text field, so
 * an address abroad is still writable.
 */
export function StateField({
  field,
  countryField,
  label,
  placeholder,
  size,
  disabled,
}: StateFieldProps): JSX.Element {
  const { t } = useLingui();

  const { value, setValue, error } = useAddressText(field);

  const country = useAtomValue(countryField?.atom ?? NO_COUNTRY) ?? "";

  const loaded = useAtomValue(statesAtom);
  const loadStates = useSetAtom(loadStatesAtom);

  useEffect(() => {
    void loadStates(country);
  }, [country, loadStates]);

  const states = loaded[country] ?? [];

  const hint = placeholder ?? t`Region`;

  if (0 === states.length) {
    return (
      <FieldFrame label={label} error={error}>
        <Input
          value={value}
          placeholder={hint}
          size={size}
          disabled={disabled}
          invalid={Boolean(error)}
          onValueChange={setValue}
        />
      </FieldFrame>
    );
  }

  const listed = states.find((state) => state.code === value);

  // A code the referential does not know is still what the address says, and
  // reads as it stands rather than being dropped.
  const selected =
    listed ?? ("" === value ? null : { id: -1, name: value, code: value });

  return (
    <FieldFrame label={label} error={error}>
      <Combobox
        items={states}
        value={selected}
        size={size}
        disabled={disabled}
        itemToStringLabel={(state: AddressState | null) => state?.name ?? ""}
        isItemEqualToValue={(a: AddressState, b: AddressState) =>
          a.code === b.code
        }
        onValueChange={(state: AddressState | null) =>
          setValue(state?.code ?? "")
        }
      >
        <ComboboxTrigger>
          <ComboboxValue placeholder={hint} />
          <ComboboxIcon />
        </ComboboxTrigger>

        <ComboboxContent
          input={
            <ComboboxInput
              placeholder={t`Search...`}
              clearable={false}
              showTrigger={false}
            />
          }
        >
          {(state: AddressState) => (
            <ComboboxItem key={state.id} value={state}>
              {state.name}
            </ComboboxItem>
          )}
        </ComboboxContent>
      </Combobox>
    </FieldFrame>
  );
}
